import type { AssetManifest } from '@/types';

/**
 * Self-hosted decoder locations (populated by `npm run setup:decoders`).
 * Served from /public so loading works offline with no CDN dependency.
 */
export const decoderPaths = {
  draco: '/draco/',
  ktx2: '/basis/',
} as const;

/**
 * Asset manifest — the ONLY place asset paths are declared.
 *
 * Replacing the Blender export requires no code changes: drop a new file at
 * `public/models/scene.glb` (or edit the `path` below). A missing model falls
 * back gracefully to the procedural placeholder so the pipeline still verifies.
 */
export const assets: AssetManifest = [
  {
    name: 'scene',
    type: 'gltf',
    // Full 4-scene model with a baked camera path — one continuous animation: the
    // camera flies through all 4 scenes' geometry, which all live in this single GLB
    // simultaneously (scene 2/3/4 meshes are suffixed `_S_2`/`_S_3`/`_S_4`). Scroll
    // Now the pipeline's default filename `scene.glb` (2026-07-17). 145 mesh nodes / 152 clips —
    // far fewer than the previous export because the user JOINED the repeated meshes in Blender
    // (the 47 `Bridge_Tile_S_3.*` → one `Bridge_Tile_S_3`; the ~95 `Stage_S_4.*` + 9
    // `Stage_Tiles_01_S_4.*` → `Stage_Tiles_01_S_4` + `Stage_Tiles_02_S_4`). Camera path timing
    // unchanged. Pairs with the baked webp atlases in `/textures/Baked_Images` (see `orvaeScene.ts`).
    path: '/models/scene.glb',
    optional: true,
  },

  // Realistic reflective water (WaterComplex/Water2): two scrolling normal maps
  // that distort the real-time planar reflection for a gentle ripple.
  { name: 'water-n0', type: 'texture', path: '/water/complex/Water_1_M_Normal.jpg', colorSpace: 'linear', flipY: true, optional: true },
  { name: 'water-n1', type: 'texture', path: '/water/complex/Water_2_M_Normal.jpg', colorSpace: 'linear', flipY: true, optional: true },

  // Cloud sea billboard sprite puff (user-supplied cloud photo/render with alpha).
  { name: 'cloud-puff', type: 'texture', path: '/textures/Cloud_Texture.png', colorSpace: 'srgb', optional: true },

  // ---- Future entries (uncomment when the assets exist) ----------------------
  // { name: 'env', type: 'hdr', path: '/hdr/studio.hdr', optional: true },
  // { name: 'baked-s1', type: 'texture', path: '/textures/s1_baked.webp', colorSpace: 'srgb' },
  // { name: 'baked-s2', type: 'texture', path: '/textures/s2_baked.webp', colorSpace: 'srgb' },
];
